import React, { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { ReactSelect } from "@services/ui-components";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { AllMenusFilter } from "./AllMenusPage";
import { initialValueFilter } from "./constants";
import { ApolloError } from "@apollo/client";
import ErrorService from "../../../services/ErrorService";
import { useMenuTypesQuery } from "@me-team/host/main/menu/graphql/menu.hooks";
import { getMenuTypeOptions } from "./options";
import { Option } from "../../../interfaces/interfaces";
import { normalizeOnBlur, normalizeOnChange } from "./helpers";

interface AllMenusFiltersProps {
    setFilters: React.Dispatch<React.SetStateAction<AllMenusFilter>>;
}

const AllMenusFilters: React.FC<AllMenusFiltersProps> = ({ setFilters }) => {
    const { t } = useTranslation();
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [search, setSearch] = useState("");
    const [selectedMenuTypeId, setSelectedMenuTypeId] = useState<Option>(null);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            setSearch(normalizeOnBlur(search));
            handleApplyFilter();
        }
    };

    const { data: menuTypesData } = useMenuTypesQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const menuTypeOptions: Option[] = getMenuTypeOptions(menuTypesData?.menuTypes, t);

    const handleApplyFilter = () => {
        setFilters({
            search: search,
            menuTypeId: selectedMenuTypeId?.value,
        });
    };

    const handleClearFilter = () => {
        setFilters(initialValueFilter);
        setSearch("");
        setSelectedMenuTypeId(null);
    };

    return (
        <>
            <Row className="gx-3">
                <Col lg={8} xl={9}>
                    <Row className="gx-3 h-100">
                        <Col md={6} className="text-primary">
                            <div className="position-relative mb-3 mb-lg-0">
                                <input
                                    id="allMenus-search-field"
                                    type="text"
                                    className="form-control text-truncate pe-5"
                                    placeholder={t("Search by name")}
                                    value={search}
                                    onChange={(e) => setSearch(normalizeOnChange(e.target.value))}
                                    onBlur={(e) => setSearch(normalizeOnBlur(e.target.value))}
                                    onKeyDown={handleSearchKeyDown}
                                />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                            </div>
                        </Col>

                        <Col md={6} className="text-primary mb-3 mb-lg-0">
                            <ReactSelect
                                id="allMenus-menuType-field"
                                placeholder={t("Type of menu")}
                                options={menuTypeOptions}
                                value={selectedMenuTypeId}
                                onChange={(selectedOption: Option) => setSelectedMenuTypeId(selectedOption)}
                                isSearchable={false}
                            />
                        </Col>
                    </Row>
                </Col>
                <Col lg={4} xl={3}>
                    <Row className="gx-3 h-100">
                        <Col xs={6}>
                            <Button
                                variant="outline-dark"
                                className="border-grey w-100 h-100 fw-normal rounded-1"
                                onClick={handleClearFilter}
                            >
                                {t("clear")}
                            </Button>
                        </Col>
                        <Col xs={6}>
                            <Button
                                variant="primary"
                                className="w-100 h-100 rounded-1 fw-normal px-2"
                                onClick={handleApplyFilter}
                            >
                                {t("Apply")}
                            </Button>
                        </Col>
                    </Row>
                </Col>
            </Row>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </>
    );
};

export default AllMenusFilters;
